'use client'

import {
  Button,
  ButtonProps,
  CustomFlowbiteTheme,
  Modal,
  ModalSizes,
} from 'flowbite-react'
import { useFormStatus } from 'react-dom'
import { ReactNode, useRef, useState } from 'react'
import { useFormFooterContext } from './FormFooterContext'

export interface FormFooterSubmitProps extends Omit<ButtonProps, 'type'> {
  confirmation?: {
    title: string
    subtitle?: ReactNode
    confirmBtnText: string
    cancelBtnText: string
    confirmBtnColor?: string
    modalSize?: string
    useHeader?: boolean
    warning?: ReactNode
  }
}

const customModalTheme: CustomFlowbiteTheme['modal'] = {
  header: {
    title: 'text-lg font-medium text-gray-900 dark:text-white',
    close: {
      base: 'ml-auto inline-flex items-center rounded-lg bg-transparent p-1 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white',
    },
  },
}

export function FormFooterSubmit({
  color = 'dark',
  disabled,
  children,
  name,
  value,
  onClick,
  confirmation,
  ...props
}: FormFooterSubmitProps) {
  const { formTrigger, isPending, startTransition } = useFormFooterContext()
  const { pending } = useFormStatus()
  const refSubmitButtom = useRef<HTMLButtonElement>(null)
  const [openMarkCompleteModal, setOpenMarkCompleteModal] = useState(false)

  const onSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    startTransition(async () => {
      onClick && onClick(event)
      const isValid = formTrigger ? await formTrigger() : true
      if (isValid) {
        refSubmitButtom.current?.click()
      }
    })
  }
  const disableBtn = pending || isPending || disabled

  return (
    <>
      {confirmation && (
        <Modal
          theme={customModalTheme}
          show={openMarkCompleteModal}
          size={confirmation.modalSize ?? 'md'}
          onClose={() => setOpenMarkCompleteModal(false)}
          popup={!confirmation.useHeader}
        >
          <Modal.Header>
            {confirmation.useHeader && confirmation.title}
          </Modal.Header>
          <Modal.Body>
            {!confirmation.useHeader && (
              <h3 className="mb-4 text-lg font-semibold text-center text-gray-900">
                {confirmation.title}
              </h3>
            )}
            <div>
              {confirmation.subtitle && (
                <div className="mb-5 text-gray-500">
                  {confirmation.subtitle}
                </div>
              )}
              {confirmation.warning && (
                <div className="mb-5">{confirmation.warning}</div>
              )}
              <div className="flex justify-center gap-4">
                <Button
                  color="light"
                  className="w-full"
                  disabled={disableBtn}
                  onClick={() => setOpenMarkCompleteModal(false)}
                >
                  {confirmation.cancelBtnText}
                </Button>
                <Button
                  color={confirmation.confirmBtnColor ?? 'failure'}
                  className="w-full"
                  disabled={disableBtn}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    setOpenMarkCompleteModal(false)
                    onSubmit(e)
                  }}
                >
                  {confirmation.confirmBtnText}
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
      <Button
        type="button"
        color={color}
        disabled={disableBtn}
        onClick={confirmation ? () => setOpenMarkCompleteModal(true) : onSubmit}
        {...props}
      >
        {children}
      </Button>
      <button
        hidden={true}
        className="hidden"
        ref={refSubmitButtom}
        type="submit"
        name={name}
        value={value}
      />
    </>
  )
}
